import React, {
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import Counter from 'components/Counter';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  DataRoomRFI,
  FileTypeRFI,
  RFI_ACTIVITY_TAB,
  RfiActivityTab,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { RfiActivityAccessDate } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { getArrayLength, getHasElementsInArray } from 'utils';
import RfiActivityMessages from './components/RfiActivityMessages';
import RfiActivityAdditionalFiles from './components/RfiActivityAdditionalFiles';
import RfiActivityEmptyState from './components/RfiActivityEmptyState';
import {
  getHasUnreadMessages,
  getHasUnreadAdditionalFiles,
  upsertRfiActivityAccessDate,
} from './utils';
import { EMPTY_ARRAY } from 'data/global';
import { SIZE } from 'models/Size';

import './RfiActivityDrawer.styles.scss';

type RfiActivityDrawerProps = {
  className?: string;
  rfi?: DataRoomRFI;
  accessDate?: RfiActivityAccessDate;
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

const RfiActivityDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  RfiActivityDrawerProps
> = ({ className, rfi, accessDate, onRfiUpdate }, ref) => {
  const classes = classNames('rfi-activity-drawer', className);

  const { t } = useTranslation();

  const { setDataRoom } = useContext(DataRoomContext);

  const [activeTab, setActiveTab] = useState<RfiActivityTab>(
    RFI_ACTIVITY_TAB.ADDITIONAL_FILES,
  );

  const messages = rfi?.brokerMessages || EMPTY_ARRAY;

  const additionalFilesCategory = rfi?.additionalDocumentsCategory;
  const additionalFiles = additionalFilesCategory?.files || EMPTY_ARRAY;

  const hasMessages = getHasElementsInArray(messages);
  const hasAdditionalFiles = getHasElementsInArray(additionalFiles);

  const hasUnreadMessages = getHasUnreadMessages(
    rfi,
    accessDate?.lastMessagesViewedAt,
  );
  const hasUnreadAdditionalFiles = getHasUnreadAdditionalFiles(
    rfi,
    accessDate?.lastAdditionalFilesViewedAt,
  );

  const onTabClick = useCallback(
    (tab: RfiActivityTab) => {
      setActiveTab(tab);

      dataRoomApi.rfi.markRfiActivityViewed(rfi._id, tab);

      setDataRoom((prev) => ({
        ...prev,
        currentMember: {
          ...prev.currentMember,
          rfiActivityAccessDates: upsertRfiActivityAccessDate(
            prev.currentMember.rfiActivityAccessDates,
            rfi._id,
            tab,
          ),
        },
      }));
    },
    [rfi._id, setDataRoom],
  );

  const onDrawerOpen = useCallback(
    () => onTabClick(RFI_ACTIVITY_TAB.ADDITIONAL_FILES),
    [onTabClick],
  );

  const onFileUpdate = useCallback(
    (updatedFile: FileTypeRFI) => {
      onRfiUpdate({
        ...rfi,
        additionalDocumentsCategory: {
          ...additionalFilesCategory,
          files: additionalFilesCategory.files.map((file) =>
            file._id === updatedFile._id ? { ...file, ...updatedFile } : file,
          ),
        },
      });
    },
    [rfi, additionalFilesCategory, onRfiUpdate],
  );

  const tabs = useMemo(
    () => [
      {
        key: RFI_ACTIVITY_TAB.ADDITIONAL_FILES,
        label: t('additionalFiles'),
        count: getArrayLength(additionalFiles),
        hasUnread: hasUnreadAdditionalFiles,
      },
      {
        key: RFI_ACTIVITY_TAB.MESSAGES,
        label: t('messages'),
        count: getArrayLength(messages),
        hasUnread: hasUnreadMessages,
      },
    ],
    [t, additionalFiles, messages, hasUnreadAdditionalFiles, hasUnreadMessages],
  );

  return (
    <SideDrawer
      className={classes}
      title={t('activity')}
      ref={ref}
      openAction={onDrawerOpen}
    >
      {!hasMessages && !hasAdditionalFiles ? (
        <RfiActivityEmptyState
          title={t('activityNothingYetTitle')}
          description={t('activityNothingYetDescription')}
        />
      ) : (
        <>
          <div className="rfi-activity-drawer__tabs">
            {tabs.map(({ key, label, count, hasUnread }) => (
              <Button
                key={key}
                variant="link"
                className={classNames({
                  active: key === activeTab,
                })}
                onClick={() => onTabClick(key)}
              >
                {label} ({count})
                {hasUnread && (
                  <Counter
                    onlyDot
                    size={SIZE.SMALL}
                    className="rfi-activity-drawer__tabs__dot"
                  />
                )}
              </Button>
            ))}
          </div>

          {activeTab === RFI_ACTIVITY_TAB.ADDITIONAL_FILES ? (
            <RfiActivityAdditionalFiles
              category={additionalFilesCategory}
              onFileUpdate={onFileUpdate}
            />
          ) : (
            <RfiActivityMessages messages={messages} />
          )}
        </>
      )}
    </SideDrawer>
  );
};

export default forwardRef(RfiActivityDrawer);
